import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Breadcrumbs,
  Link as MUILink,
} from "@mui/material";
import { Link } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { Modal } from "antd";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import {
  getStudents,
  deleteStudent,
} from "../redux/actions/admin/studentAction";
import PageTitle from "../Components/PageTitle";
import Table from "./common/Table";
import { getCourses } from "../redux/actions/admin/courseAction";

const AllStudentList = () => {
  const dispatch = useDispatch();
  const { students, loading } = useSelector((state) => state.studentData);
  const { courses, error } = useSelector((state) => state.courses);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [allStudents, setAllStudents] = useState([]);

  useEffect(() => {
    dispatch(getStudents());
  }, [dispatch]);

  useEffect(() => {
    setAllStudents(students);
  }, [students]);

  useEffect(() => {
    dispatch(getCourses());
  }, [dispatch]);

  useEffect(() => {
    setFilteredCourses(courses);
  }, [courses]);

  // Add this function to your component
  const getCourseFees = (courseId) => {
    if (!courseId || !filteredCourses.length) return "N/A";

    const course = filteredCourses.find((course) => course._id === courseId);
    return course ? `₹ ${course.fees}` : "N/A";
  };

  // Filter users based on search term
  const filteredUsers = allStudents.filter((user) => {
    return (
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // delete User
  const { confirm } = Modal;
  const deleteUser = async (e, id) => {
    confirm({
      title: "Are you sure you want to delete this Student?",
      content: "This action cannot be undone. Please confirm.",
      okText: "Yes, Delete",
      cancelText: "No, Cancel",
      onOk: async () => {
        await dispatch(deleteStudent(id));
        await dispatch(getStudents());
      },
      onCancel() {
        console.log("Delete operation canceled");
      },
    });
  };

  const AllStudentsGeneratePDF = (students) => {
    Modal.confirm({
      title: "Confirm Download",
      content:
        "Would you like to download the details of all students as a PDF file?",
      okText: "Yes, Download",
      cancelText: "No, Cancel",
      onOk: () => {
        const doc = new jsPDF();

        // Title
        doc.setFontSize(16);
        doc.setTextColor(0, 51, 102);
        doc.text("All Student Report", 105, 20, { align: "center" });

        // Table columns
        const columns = [
          { header: "#", dataKey: "index" },
          { header: "Name", dataKey: "name" },
          { header: "Email", dataKey: "email" },
          { header: "Phone", dataKey: "phone" },
          { header: "Parent Phone", dataKey: "parentPhone" },
          { header: "Paid Fees", dataKey: "paidFees" },
          { header: "Course Fees", dataKey: "courseFees" },
          { header: "Join Date", dataKey: "joinDate" },
        ];

        // Table data
        const studentData = students.map((student, index) => {
          const course = filteredCourses.find(
            (c) => c._id === student.courseId
          );
          return {
            index: index + 1,
            name: student.name || "N/A",
            email: student.email || "N/A",
            phone: student.phone || "N/A",
            parentPhone: student.parentPhone || "N/A",
            paidFees: `${student.feesDeposit || "N/A"}`,
            courseFees: course ? `${course.fees}` : "N/A",
            joinDate: student.createdAt
              ? moment(student.createdAt).format("MM/DD/YYYY")
              : "N/A",
          };
        });

        // Add table to the PDF
        autoTable(doc, {
          columns: columns,
          body: studentData,
          startY: 30,
          styles: {
            fontSize: 10,
            cellPadding: 2,
            overflow: "linebreak",
          },
          headStyles: {
            fillColor: [0, 51, 102],
            textColor: 255,
            fontStyle: "bold",
          },
        });

        // Save the generated PDF
        doc.save("All_Students_Details.pdf");
      },
      onCancel() {
        console.log("Download canceled");
      },
    });
  };

  const generatePDF = (student) => {
    Modal.confirm({
      title: "Confirm Download",
      content:
        "Would you like to download the details of the student as a PDF file?",
      okText: "Yes, Download",
      cancelText: "No, Cancel",
      onOk: () => {
        const doc = new jsPDF();
        const course = filteredCourses.find((c) => c._id === student.courseId);

        // Set the title
        doc.setFontSize(16);
        doc.setTextColor(0, 51, 102);
        doc.text("Student Details", 105, 10, { align: "center" });

        // Draw a line below the title
        doc.setDrawColor(0, 51, 102);
        doc.line(10, 15, 200, 15);

        // Set basic information
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);

        const studentInfo = [
          ["Student Name:", student.name || "N/A"],
          ["Student Email:", student.email || "N/A"],
          ["Student Phone:", student.phone || "N/A"],
          ["Parent Name:", student.parentName || "N/A"],
          ["Parent Email:", student.parentEmail || "N/A"],
          ["Parent Phone:", student.parentPhone || "N/A"],
          ["Course Name:", course?.name || "N/A"],
          ["Course Fees:", course ? `${course.fees}` : "N/A"],
          ["Paid Fees:", `₹ ${student.feesDeposit || "N/A"}`],
          [
            "Pending Fees:",
            course ? `${course.fees - (student.feesDeposit || 0)}` : "N/A",
          ],
          [
            "Joined On:",
            student.createdAt
              ? new Date(student.createdAt)
                  .toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                  .replace(/ /g, "-")
              : "N/A",
          ],
        ];

        // Create a table for student info
        autoTable(doc, {
          startY: 25,
          body: studentInfo,
          theme: "grid",
          head: [["Field", "Value"]],
          styles: {
            cellPadding: 2,
            fontSize: 10,
          },
          columnStyles: {
            0: { fontStyle: "bold", cellWidth: 50 },
            1: { cellWidth: "auto" },
          },
          didDrawPage: () => {
            doc.setFontSize(14);
            doc.setTextColor(0, 51, 102);
            doc.text("Student Information", 14, 20);
          },
        });

        // Save the PDF
        doc.save(`${student.name || "student"}_details.pdf`);
      },
      onCancel() {
        console.log("Download canceled");
      },
    });
  };

  const columns = [
    {
      field: "rowNumber",
      headerName: "#",
      width: 10,
      sortable: false,
    },
    {
      field: "Actions",
      width: 120,
      renderCell: (value) => (
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton
            aria-label="delete"
            onClick={(e) => deleteUser(e, value?.row?._id)}
          >
            <DeleteIcon sx={{ color: "red" }} />
          </IconButton>
          <IconButton
            aria-label="download"
            onClick={() => generatePDF(value.row)}
          >
            <DownloadIcon sx={{ color: "green" }} />
          </IconButton>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "Full Name",
      width: 180,
    },
    {
      field: "email",
      headerName: "E-mail",
      width: 180,
    },
    {
      field: "phone",
      headerName: "Student Phone",
      width: 180,
    },
    {
      field: "parentPhone",
      headerName: "Parent Phone",
      width: 180,
    },
    {
      field: "feesDeposit",
      headerName: "Fees",
      width: 100,
      renderCell: (params) => `₹ ${params.row.feesDeposit || "N/A"}`,
    },
    {
      field: "courseId",
      headerName: "Course Fees",
      width: 180,
      renderCell: (params) => getCourseFees(params.row.courseId),
    },
    {
      field: "createdAt",
      headerName: "Create Date",
      width: 180,
      renderCell: (params) =>
        moment(params?.row?.createdAt).format("MM/DD/YYYY"),
    },
  ];

  return (
    <div>
      <PageTitle
        title="All Students - Admin Panel | Shree Mangalam School"
        description="Browse and manage the complete list of students enrolled at Shree Mangalam School through the Admin Panel. Search, filter, and access student profiles and course details easily."
        url="/admin/student/list"
        keywords="student list admin, Shree Manglam students, school student directory, manage student data, view student records, admin panel students"
      />
      <Box>
        <Typography
          variant="h5"
          align="center"
          gutterBottom
          sx={{
            mb: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#2C3E50",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          All Student Details
        </Typography>

        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            mb: 3,
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <MUILink
            component={Link}
            to="/dashboard"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Dashboard
          </MUILink>
          <MUILink
            component={Link}
            to="/admin/batch"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Batch
          </MUILink>
          <Typography sx={{ color: "primary.main" }}>
            Student Details
          </Typography>
        </Breadcrumbs>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Name or Email"
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton>
                <SearchIcon sx={{ color: "#1976d2" }} />
              </IconButton>
            ),
          }}
          sx={{
            mb: 3,
            mt: 3,
            bgcolor: "white",
            borderRadius: "20px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={() => AllStudentsGeneratePDF(allStudents)}
          startIcon={<DownloadIcon />}
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            borderRadius: "5px",
            textTransform: "none",
            fontFamily: "Poppins, sans-serif",
            letterSpacing: ".1rem",
          }}
        >
          Export to PDF
        </Button>
        <Table data={filteredUsers} columns={columns} loading={loading} />
      </Box>
    </div>
  );
};

export default AllStudentList;
